import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Brain, ArrowRight, CheckCircle, Sparkles, TrendingUp, Bot,
  Database, Cpu, Radio, CircuitBoard, Layers, Target, Zap
} from 'lucide-react';

const AIMaturitySection = () => {
  const aiLevels = [
    { level: 5, name: 'Physical AI', description: 'Real-world sensing & action', icon: Radio, color: 'from-red-500 to-red-600' },
    { level: 4, name: 'Agentic AI', description: 'Multi-agent coordination', icon: CircuitBoard, color: 'from-amber-500 to-amber-600' },
    { level: 3, name: 'AI Agents', description: 'Task orchestration', icon: Bot, color: 'from-emerald-500 to-emerald-600' },
    { level: 2, name: 'Generative AI', description: 'Reasoning & synthesis', icon: Sparkles, color: 'from-purple-500 to-purple-600' },
    { level: 1, name: 'Predictive AI', description: 'Forecasting outcomes', icon: TrendingUp, color: 'from-blue-500 to-blue-600' },
    { level: 0, name: 'Data Platform', description: 'AI-ready foundation', icon: Database, color: 'from-gray-500 to-gray-600' },
  ];

  const features = [
    '6 comprehensive dimensions',
    'AI chatbot consultation',
    'Industry benchmarking',
    '12-month roadmap',
    'ROI analysis',
    'C-suite ready insight report'
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-full px-4 py-1.5 mb-6">
            <Brain className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">NEW: AI Maturity Assessment</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            AI Maturity Assessment
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Evaluate your organization&apos;s AI readiness across 6 maturity levels - from foundational 
            data platforms to physical AI systems. Industry-specific insights powered by AI.
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - AI Maturity Ladder */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-400" />
              The AI Maturity Ladder
            </h3>
            <div className="space-y-2">
              {aiLevels.map((item) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={item.level}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 hover:bg-white/10 hover:border-indigo-500/30 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-gray-500 bg-white/10 px-1.5 py-0.5 rounded">L{item.level}</span>
                          <h4 className="font-semibold text-white text-sm">{item.name}</h4>
                        </div>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right side - Features & CTA */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              What You&apos;ll Get
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* AI Chatbot Preview */}
            <div className="bg-black/20 rounded-xl p-4 mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">AI Consultant</p>
                  <p className="text-sm text-white">Chat with our AI to provide context for personalized insights</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded-full">MLOps Maturity</span>
                <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">Model Governance</span>
                <span className="text-xs bg-pink-500/20 text-pink-300 px-2 py-1 rounded-full">Data Architecture</span>
              </div>
            </div>

            {/* CTA Button */}
            <Link 
              to="/ai-maturity"
              className="group flex items-center justify-center gap-3 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white font-semibold py-4 px-6 rounded-xl transition-all shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30"
              data-testid="launch-ai-maturity"
            >
              <Zap className="w-5 h-5" />
              <span>Assess Organization&apos;s AI Maturity</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <p className="text-center text-xs text-gray-500 mt-3">
              Takes ~15 minutes • AI-powered analysis • Industry benchmarks
            </p>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '6', label: 'AI Maturity Levels' },
            { value: '6', label: 'Assessment Dimensions' },
            { value: '33', label: 'Industries Supported' },
            { value: '100+', label: 'Industry KPIs' }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AIMaturitySection;
